import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useAuiState } from "@assistant-ui/react";
import { useMessageFeedback } from "./AgentProvider";
import { ANALYTICS_EVENTS, trackEvent } from "@repo/ui/lib/analytics";
import { getBackendMessageId, getMessageSubAgents } from "./messageIdentity";
import type { FeedbackThumbState } from "./MessageFeedback.types";
import { messageFeedbackStyles as s } from "./MessageFeedback.styles";
import type { FeedbackRating } from "./agentApi";

/** Reported in place of a sub-agent for history messages, which carry no routing info. */
const SUB_AGENT_UNAVAILABLE = "unavailable_not_live";

function thumbState(rating: FeedbackRating | undefined, type: FeedbackRating): FeedbackThumbState {
  if (rating !== type) return "idle";
  return type === "positive" ? "selectedPositive" : "selectedNegative";
}

/**
 * Rating control for one assistant message, showing only confirmed server state: the persisted
 * rating (from history) or the last one the server accepted — never an optimistic click. A failed
 * write keeps the previous rating and offers Retry. Renders nothing without a backend id, which
 * arrives on the stream's message_start (so even failed/aborted turns stay rateable) or from
 * persisted history.
 */
export function MessageFeedback() {
  const message = useAuiState((s) => s.message);
  const { sessionId, submitFeedback } = useMessageFeedback();

  const backendId = getBackendMessageId(message);
  const persistedRating = message.metadata?.submittedFeedback?.type;

  // Server-confirmed rating this mount; takes precedence over the persisted one.
  const [confirmedRating, setConfirmedRating] = useState<FeedbackRating | undefined>(undefined);
  // In-flight rating — both buttons are disabled while set.
  const [pendingRating, setPendingRating] = useState<FeedbackRating | null>(null);
  // Failed rating awaiting Retry.
  const [failedRating, setFailedRating] = useState<FeedbackRating | null>(null);

  const rating = confirmedRating ?? persistedRating;

  if (!backendId) return null;

  const submit = async (type: FeedbackRating) => {
    if (pendingRating) return;
    setPendingRating(type);
    setFailedRating(null);
    try {
      await submitFeedback(backendId, type);
      setConfirmedRating(type);
      // Only live-streamed messages record their sub-agents; say so explicitly otherwise.
      const subAgents = getMessageSubAgents(message);
      trackEvent(ANALYTICS_EVENTS.feedbackGiven, {
        sessionId,
        messageId: backendId,
        rating: type === "positive" ? "thumbs_up" : "thumbs_down",
        isLiveMessage: subAgents !== undefined,
        subAgent: !subAgents
          ? SUB_AGENT_UNAVAILABLE
          : subAgents.length > 0
            ? subAgents.join(", ")
            : "none",
      });
    } catch {
      setFailedRating(type);
    } finally {
      setPendingRating(null);
    }
  };

  return (
    <div className={s.root}>
      <button
        type="button"
        aria-label="Helpful"
        aria-pressed={rating === "positive"}
        disabled={pendingRating !== null}
        onClick={() => submit("positive")}
        className={s.thumb({ state: thumbState(rating, "positive") })}
      >
        <ThumbsUp size={13} />
      </button>
      <button
        type="button"
        aria-label="Not helpful"
        aria-pressed={rating === "negative"}
        disabled={pendingRating !== null}
        onClick={() => submit("negative")}
        className={s.thumb({ state: thumbState(rating, "negative") })}
      >
        <ThumbsDown size={13} />
      </button>
      <span className={s.status} aria-live="polite">
        {pendingRating ? "Saving…" : failedRating ? "Couldn't save." : ""}
      </span>
      {failedRating && (
        <button
          type="button"
          onClick={() => submit(failedRating)}
          className={s.retryBtn}
          aria-label="Retry saving rating"
        >
          Retry
        </button>
      )}
    </div>
  );
}
